/**
 * Single Color Mode component
 *
 * When active, the pricing grid shows only:
 *   - The currently selected (clicked) color row
 *   - Any rows that have nonzero quantity values entered
 *   - Any rows that were pre-populated from the cart at page load (never hidden)
 *
 * When a swatch is clicked in this mode the selected color's rows are moved
 * to the top of the table and all empty, non-cart rows are hidden.
 *
 * When deactivated all rows are restored to their original DOM order and shown.
 */

import {
  ensureProductLoaded,
  ensureProductsLoaded,
  getFamilyOrder,
} from "./lazyProductLoader.js";
import { reassignMobileTabindexes } from "./mobileMirror.js";
import {
  clearPin,
  getFamilyRowOrder,
  pinColorToTop,
  scheduleDesktopTabindexReassign,
} from "./colorPin.js";
import {
  getShowAllColorsPreference,
  saveShowAllColorsPreference,
} from "../services/singleColorModeStorage.js";
import { isMobileViewport } from "../utils/viewport.js";

let isModeActive = false;

/**
 * Product handles whose rows had nonzero quantities when pre-populated from
 * the cart.  These rows are never hidden even if the user clears the inputs.
 */
const cartPrepopulatedHandles = new Set<string>();

// ─── Public API ──────────────────────────────────────────────────────────────

/** Returns true when single color mode is currently active. */
export function isSingleColorModeActive(): boolean {
  return isModeActive;
}

/**
 * Called once after prePopulateFromCart() resolves.
 * Scans all rows and records handles that received nonzero cart quantities so
 * those rows are never hidden by single color mode.
 */
export function markCartPrepopulatedRows(): void {
  const seen = new Set<string>();

  $(".warehouse_row").each(function (): void {
    const handle = $(this).attr("data-product-handle");
    if (!handle || seen.has(handle)) return;
    seen.add(handle);

    const hasQty = hasAnyNonzeroInput(handle);
    if (hasQty) {
      cartPrepopulatedHandles.add(handle);
    }
  });
}

/**
 * Apply single color mode visibility for the given selected product handle.
 * Moves the selected color's rows to the top of the tbody and hides all rows
 * that have no current values and were not pre-populated from the cart.
 *
 * Pass undefined to show all rows (no color selected yet).
 */
export function applySingleColorMode(selectedHandle?: string): void {
  if (!isModeActive) return;

  if (!selectedHandle) {
    // No color selected: show everything, remove filtering
    $(".inventory_master_table").removeClass("filtering-active");
    $(".warehouse_row").removeClass("show-this-color");
    return;
  }

  // Clear any stale filter classes first; don't add filtering-active yet.
  $(".warehouse_row").removeClass("show-this-color");

  // Determine which handles to show and count how many rows actually matched.
  let matchedRowCount = 0;
  const allHandles = collectAllHandles();
  allHandles.forEach((handle) => {
    if (
      handle === selectedHandle ||
      cartPrepopulatedHandles.has(handle) ||
      hasAnyNonzeroInput(handle)
    ) {
      const $rows = $(`.warehouse_row[data-product-handle="${handle}"]`);
      if ($rows.length > 0) {
        $rows.addClass("show-this-color");
        matchedRowCount += $rows.length;
      }
    }
  });

  // Safety: if nothing matched, don't activate filtering — an empty pricing
  // grid is worse than showing all rows. This guards against races where the
  // active swatch handle refers to a row that isn't in the DOM yet.
  if (matchedRowCount === 0) {
    $(".inventory_master_table").removeClass("filtering-active");
    console.warn(
      "[singleColorMode] No rows matched selectedHandle=%s — leaving filter off",
      selectedHandle
    );
    return;
  }

  // Activate filtering now that we know at least one row is visible.
  $(".inventory_master_table").addClass("filtering-active");

  // Move selected color's rows to the top of the tbody, then sort the rest
  // alphabetically by color name. Mirrors the mobile sort below so the two
  // breakpoints stay consistent and the user doesn't see a flash on resize.
  const $tbody = $(".inventory_table_tbody");
  $(`.warehouse_row[data-product-handle="${selectedHandle}"]`).prependTo($tbody);
  reorderTbodyActiveFirstAlpha(selectedHandle);

  // Q-14: mobile cards must reflect the same active-first-alpha order. The
  // mobile-only flag inside the helper short-circuits when no mobile cards
  // are present, so this is a no-op on desktop-only renders.
  reorderMobileCardsActiveFirstAlpha(selectedHandle);

  // Reassign mobile tabindexes from the new DOM order — lazy-loaded cards
  // would otherwise collide on product_index=0's tabindex block, breaking
  // column-major-within-card tab order. The desktop table needs the same
  // treatment for the same reason: the reorder above just moved groups out of
  // the family order their Liquid-assigned tabindexes were derived from.
  reassignMobileTabindexes();
  scheduleDesktopTabindexReassign();

  // Keep the mobile <select> in sync so swatch clicks, deep-link activations,
  // and "Reset Filters" all show the right active color in the picker.
  const $mobileSelect = $("#mobileColorSelect");
  if ($mobileSelect.length > 0 && $mobileSelect.val() !== selectedHandle) {
    $mobileSelect.val(selectedHandle);
  }
}

/**
 * Re-append every non-active warehouse_row group to the tbody in alphabetical
 * order by `data-inventory-color`. The active group is left wherever it sits
 * (typically at the top after the prepend in applySingleColorMode). jQuery's
 * appendTo on existing nodes moves them, so each call ends with the tbody
 * containing exactly the rows it started with — just reordered.
 */
function reorderTbodyActiveFirstAlpha(activeHandle: string): void {
  const $tbody = $(".inventory_table_tbody");
  if ($tbody.length === 0) return;

  const handles = collectAllHandles().filter((h) => h !== activeHandle);
  handles.sort((a, b) =>
    colorNameForHandle(a).localeCompare(colorNameForHandle(b)),
  );
  handles.forEach((h) => {
    $(`.warehouse_row[data-product-handle="${h}"]`).appendTo($tbody);
  });
}

/**
 * Reorder .mobile-inventory-product cards so the active card is first and
 * the rest follow in alphabetical order by `data-mobile-card-color`. Used
 * on mobile where single-color mode is locked on but cart-prepopulated
 * colors stay visible; without this the active card would land wherever
 * its server-render position put it, forcing the user to scroll past
 * other colors to reach the picker target.
 */
function reorderMobileCardsActiveFirstAlpha(
  activeHandle: string | undefined,
): void {
  const $list = $(".mobile-inventory-list");
  if ($list.length === 0) return;
  const cards = $list
    .children(".mobile-inventory-product")
    .toArray() as HTMLElement[];
  if (cards.length === 0) return;

  const others = cards.filter(
    (c) => c.dataset.productHandle !== activeHandle,
  );
  others.sort((a, b) =>
    (a.dataset.mobileCardColor ?? "").localeCompare(
      b.dataset.mobileCardColor ?? "",
    ),
  );

  const active = activeHandle
    ? cards.find((c) => c.dataset.productHandle === activeHandle)
    : undefined;

  const ordered: HTMLElement[] = active ? [active, ...others] : others;
  ordered.forEach((c) => $list.append(c));
}

/**
 * Read the color name for a product handle from any of its desktop rows.
 * Falls back to the handle itself if no row is found (defensive — only
 * happens when called for an unloaded lazy product, which the caller
 * filters out anyway via collectAllHandles).
 */
function colorNameForHandle(handle: string): string {
  const $row = $(
    `.warehouse_row[data-product-handle="${handle}"]`,
  ).first();
  return ($row.attr("data-inventory-color") || handle).toLowerCase();
}

/**
 * Force single color mode ON — checkbox unchecked, state active, filter
 * applied to the currently active swatch. Used by "Reset Filters" to
 * return to the default view state regardless of any prior user toggle.
 * Does NOT overwrite the user's persisted preference in localStorage —
 * their choice is restored on the next page load.
 */
export function enterSingleColorMode(selectedHandle?: string): void {
  isModeActive = true;
  $("#showAllColorsToggle").prop("checked", false);

  // Drop the show-all pin. State only — applySingleColorMode() below re-appends
  // every group, so restoring the pinned one first would be work its own
  // reorder immediately overwrites.
  clearPin();

  const handle =
    selectedHandle ??
    $(".Product_swatch_colors .swatch-option.active-color").attr(
      "data-product_handle",
    );

  applySingleColorMode(handle);
  updateColorDropdownMode();
}

/**
 * Reset single color mode: uncheck the checkbox, show all rows in original order.
 * Safe to call even when mode is not active.
 */
export function resetSingleColorMode(): void {
  isModeActive = false;
  $("#showAllColorsToggle").prop("checked", true);
  saveShowAllColorsPreference(true);
  // Clear before restoring: restoreOriginalOrder() puts every group back in
  // family order, which un-pins the DOM on its own.
  clearPin();
  restoreOriginalOrder();
  $(".inventory_master_table").removeClass("filtering-active");
  $(".warehouse_row").removeClass("show-this-color");
  updateColorDropdownMode();
}

/**
 * Deactivate single color mode and show all colors.
 * Shared by the change handler and the initial load path when the user's
 * stored preference is "show all".
 */
function deactivateAndShowAll(): void {
  isModeActive = false;
  restoreOriginalOrder();
  $(".inventory_master_table").removeClass("filtering-active");
  $(".warehouse_row").removeClass("show-this-color single_related");

  // Reset dropdown label to "All Colors"
  $(".selected_colors").html(
    '<span class="cf-dropdown__label-prefix">Colors:</span>All Colors'
  );
  $("#resetGrid").addClass("is-disabled");

  // Clear any residual checked state in the color dropdown. Otherwise a
  // prior single-color selection made via the dropdown (or by the old
  // swatch-click-filters-grid behavior) could later cause applyColorFilter
  // to re-filter even though the user has asked to show everything.
  $("#colorDropdown_options input[type='checkbox']").prop("checked", false);
  $("#selectAll input[type='checkbox']").prop("checked", false);

  // Carry the active color over as a pinned row rather than resetting the
  // swatch highlight to the first swatch: checking this box asks to see MORE
  // colors, not to lose the one you were on. Pin before the lazy loads below
  // so the hoist is instant — the pinned color's rows are already in the DOM
  // (single color mode was displaying them), and colorPin re-asserts row 1 as
  // the rest of the family streams in.
  //
  // The dropdown label stays "All Colors": the pin is a sort, not a filter,
  // and every color really is visible.
  // Falls back to the first swatch when nothing is highlighted yet, preserving
  // the old "some swatch is always active" invariant: colorFilter's ready
  // handler normally sets it, but that fires asynchronously.
  if ($(".Product_swatch_colors .swatch-option.active-color").length === 0) {
    $(".Product_swatch_colors .swatch-option").first().addClass("active-color");
  }

  const activeHandle = $(".Product_swatch_colors .swatch-option.active-color")
    .attr("data-product_handle");
  if (activeHandle) {
    pinColorToTop(activeHandle);
  }

  // Lazy-load all remaining products so the full grid is visible.
  const allHandles = getFamilyOrder();
  if (allHandles.length > 0) {
    void ensureProductsLoaded(allHandles);
  }
}

/**
 * Toggle the color dropdown's checkbox visibility based on single-color mode.
 * When active, multi-select checkboxes are hidden since only one color is shown
 * at a time; the "Show All Colors" option is also hidden.
 */
function updateColorDropdownMode(): void {
  if (isModeActive) {
    $("#allColors").addClass("dropdown-single-mode");
  } else {
    $("#allColors").removeClass("dropdown-single-mode");
  }
}

/**
 * Initialize the single color mode checkbox handler.
 * Must be called once after the DOM is ready.
 */
export function initSingleColorMode(): void {
  // On mobile the toggle is hidden and the mode is locked on — don't bind a
  // change handler or read the stored preference.
  if (isMobileViewport()) {
    isModeActive = true;
    $("#showAllColorsToggle").prop("checked", false);
    return;
  }

  // The UI checkbox is "Show Multiple Colors" — inverse of single color mode.
  // Checked = show everything (mode off); unchecked = single color mode active.
  // This is a provisional value; activateInitialSingleColorMode() will override
  // it based on the user's stored preference once the grid is ready.
  isModeActive = !$("#showAllColorsToggle").is(":checked");

  $("#showAllColorsToggle").on("change", function (): void {
    const showAll = $(this).is(":checked");
    saveShowAllColorsPreference(showAll);
    isModeActive = !showAll;

    if (!isModeActive) {
      deactivateAndShowAll();
    } else {
      // Activating: immediately apply filter for the currently active swatch.
      // clearPin() is state-only — applySingleColorMode() rebuilds row order
      // wholesale, so the pinned group gets repositioned regardless.
      clearPin();
      const handle = $(".Product_swatch_colors .swatch-option.active-color")
        .attr("data-product_handle");
      applySingleColorMode(handle);
      syncDropdownLabelToHandle(handle);
    }

    updateColorDropdownMode();
  });
}

/**
 * Called after cart pre-population and markCartPrepopulatedRows().
 * Honors the user's stored "Show Multiple Colors" preference from localStorage.
 * Default (no stored preference) is single color mode ON.
 */
export async function activateInitialSingleColorMode(): Promise<void> {
  // On mobile, single-color mode is locked on regardless of stored preference.
  const pref = isMobileViewport() ? false : getShowAllColorsPreference();

  if (pref === true) {
    // User previously chose "Show Multiple Colors" — honor it and show all.
    $("#showAllColorsToggle").prop("checked", true);
    deactivateAndShowAll();
    updateColorDropdownMode();
    return;
  }

  const handle = $(".Product_swatch_colors .swatch-option.active-color")
    .attr("data-product_handle");

  // If lazy loading is active, the selected color's row may not be in the DOM
  // yet. Wait for it before applying the filter — otherwise applySingleColorMode
  // would find no matching row and its safety check would bail, leaving the grid
  // unfiltered. This is a no-op when the row is already server-rendered.
  if (handle) {
    try {
      await ensureProductLoaded(handle);
    } catch (error) {
      console.error(
        "[singleColorMode] Failed to preload initial color row:",
        error
      );
    }
  }

  isModeActive = true;
  $("#showAllColorsToggle").prop("checked", false);
  applySingleColorMode(handle);
  syncDropdownLabelToHandle(handle);
  updateColorDropdownMode();
}

// ─── Private helpers ─────────────────────────────────────────────────────────

/**
 * Re-append all rows in the canonical family order, restoring original layout.
 * The order itself is captured by colorPin.initColorPin(), which runs before
 * anything reorders the tbody — both row-moving paths read the same list so
 * they can't disagree about what "original order" means.
 */
function restoreOriginalOrder(): void {
  const $tbody = $(".inventory_table_tbody");
  getFamilyRowOrder().forEach((handle) => {
    $(`.warehouse_row[data-product-handle="${handle}"]`).appendTo($tbody);
  });
}

/** Collect every unique product handle present in the tbody. */
function collectAllHandles(): string[] {
  const seen = new Set<string>();
  const handles: string[] = [];
  $(".warehouse_row").each(function (): void {
    const handle = $(this).attr("data-product-handle");
    if (handle && !seen.has(handle)) {
      seen.add(handle);
      handles.push(handle);
    }
  });
  return handles;
}

/**
 * Updates the color dropdown label to show the name of the given product handle,
 * or resets to "All Colors" if no handle is provided.
 */
function syncDropdownLabelToHandle(handle: string | undefined): void {
  if (!handle) return;
  const colorName = $(
    `#colorDropdown_options li[data-product_handle="${handle}"] .cf-dropdown__option-text`
  ).text().trim();
  if (colorName) {
    $(".selected_colors").html(colorName);
  }
}

/**
 * Returns true if any quantity input belonging to the given product handle
 * currently has a nonzero value.
 */
function hasAnyNonzeroInput(productHandle: string): boolean {
  return $(`.warehouse_row[data-product-handle="${productHandle}"] .variant_qty_input_directship`)
    .toArray()
    .some((el) => {
      const val = parseInt(($(el).val() as string) ?? "0", 10);
      return !isNaN(val) && val > 0;
    });
}
